/**
 * What the global Kory tray is about on the current page.
 *
 * A page declares its subject with ``useKoryPageSubject`` (the DCC deal page:
 * Context on that deal). Pages that declare nothing get their route family's
 * mode from ``ROUTE_MODES`` (scorecards and finance explain), else Everything.
 *
 * - ``host``: the page carries Kory's brief (Overview with Chief of Staff on). It
 *   hands its rows to the global tray (``useKoryTrayBrief``), or else carries its
 *   own tray and the global one hides.
 * - ``context``: the tray holds the subject's to-dos and talks about the subject (a Drive).
 * - ``everything``: the tray holds the person's queue.
 * - ``explain``: a numbers page; the tray leads with "explain this" prompts.
 * - ``meeting``: a live meeting surface (no page registers it yet).
 * - ``hidden``: no tray.
 *
 * A to-do appears once per screen, and the page wins: a page that lists to-dos itself declares which
 * (``shows``), and the tray leaves them out (``trayExcludes``). Overview and /todos list all of them, so
 * the tray holds no list there (Overview's tray keeps its one picked card). A DCC deal page lists that
 * deal's, so the tray holds the person's other next steps.
 */

import { isSalesPerformancePath } from "@/lib/sales-performance-paths";
export type KoryPageMode = "host" | "context" | "everything" | "explain" | "meeting" | "hidden";

export interface KoryPageSubject {
  /** The noun the page is about (``deal``), or ``page`` for none. */
  type: string;
  id?: string;
  /** How the tray names the subject ("Skyline Towers"). */
  label?: string;
  mode: KoryPageMode;
  /** The to-dos the page itself lists: all of the person's, or one deal's. */
  shows?: "all_my_todos" | { dealId: string };
}

export const DEFAULT_SUBJECT: KoryPageSubject = { type: "page", mode: "everything" };

/** Pages that already own the bottom of the screen (a fixed or sticky commit
 *  dock or action bar), where a docked tray would sit on top of their controls. */
const BOTTOM_BAR_ROUTES = ["/daily-huddle", "/sales-operations/wip-mobile", "/admin/onboarding/new", "/safety/6s"];

/** Never a tray here, whatever the page declares: Kory's own chat, the call
 *  simulator, share links, and the bottom-bar pages above. */
export function routeHidesKoryTray(pathname: string): boolean {
  return (
    pathname === "/kory" ||
    pathname === "/kory/chat" ||
    pathname.startsWith("/call-simulator/") ||
    pathname.startsWith("/share/") ||
    BOTTOM_BAR_ROUTES.some((prefix) => matches(pathname, prefix))
  );
}

/** Route families whose pages declare nothing themselves. First match wins. */
const ROUTE_MODES: { prefix: string; mode: KoryPageMode }[] = [
  { prefix: "/scorecards", mode: "explain" },
  { prefix: "/portfolio-scorecard", mode: "explain" },
  { prefix: "/ai-scorecard", mode: "explain" },
  { prefix: "/sales-operations", mode: "explain" },
  { prefix: "/finance", mode: "explain" },
];

/** What a page needs beside a docked Kory tray when nothing declares it. */
const DEFAULT_NEEDS_WIDTH = 720;

/** Short of these widths the tray floats over the page instead of docking beside it. First match wins.
 *  Scorecards need none: a docked tray always docks there, and the grid scrolls sideways beside it. */
const ROUTE_NEEDS_WIDTH: { prefix: string; px: number }[] = [
  { prefix: "/scorecards", px: 0 },
  { prefix: "/portfolio-scorecard", px: 0 },
  { prefix: "/service-scorecard", px: 0 },
  { prefix: "/sales-marketing-scorecard", px: 0 },
  { prefix: "/inventory-scorecard", px: 0 },
  { prefix: "/ai-scorecard", px: 0 },
  { prefix: "/csr-scorecard", px: 0 },
  { prefix: "/mna/scorecard", px: 0 },
  { prefix: "/meetings", px: 900 },
];

/** The width (px) this route needs beside a docked tray. */
export function routeNeedsWidth(pathname: string): number {
  // Sales Performance is a scorecard; the section's other pages (the classic page, WIP) are not.
  if (isSalesPerformancePath(pathname)) return 0;
  return ROUTE_NEEDS_WIDTH.find((r) => matches(pathname, r.prefix))?.px ?? DEFAULT_NEEDS_WIDTH;
}

function matches(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** The subject the tray shows: hidden routes first, then the page's own
 *  declaration, then its route family, then Everything. */
export function resolveKoryPageSubject(pathname: string, declared: KoryPageSubject | null): KoryPageSubject {
  if (routeHidesKoryTray(pathname)) return { type: "page", mode: "hidden" };
  if (declared) return declared;
  const family = ROUTE_MODES.find((r) => matches(pathname, r.prefix));
  return family ? { type: "page", mode: family.mode } : DEFAULT_SUBJECT;
}

/** The global tray shows on this subject (Host and Hidden both hand the page back). */
export function trayShows(subject: KoryPageSubject): boolean {
  return subject.mode !== "host" && subject.mode !== "hidden";
}

/** The page already lists this to-do, so the tray leaves it out. */
export function trayExcludes(subject: KoryPageSubject, todo: { hickory_deal?: { id: string } | null }): boolean {
  const shows = subject.shows;
  if (!shows) return false;
  if (shows === "all_my_todos") return true;
  return todo.hickory_deal?.id === shows.dealId;
}

/** The page lists every to-do of the person's, so the tray holds no list of its own. */
export function pageShowsAllTodos(subject: KoryPageSubject): boolean {
  return subject.shows === "all_my_todos";
}

/** The Drive this page is about (its id), or null: the tray talks to that Drive. */
export function driveSubjectId(subject: KoryPageSubject): string | null {
  return subject.type === "drive" && subject.mode === "context" ? (subject.id ?? null) : null;
}

/** The model-only line a tray message carries, so Kory knows what the person
 *  was looking at. Null when the page has no subject beyond itself. */
export function subjectContextLine(subject: KoryPageSubject): string | null {
  if (subject.type === "page" || !subject.id) return null;
  const name = subject.label ? ` "${subject.label}"` : "";
  return `The user is asking from the Kory tray while viewing ${subject.type} ${subject.id}${name}.`;
}

/** Explain-mode openers when no value is attached yet. */
export const EXPLAIN_PROMPTS = ["Explain this page", "What changed this week?", "What should I look at first?"];

/** ``K`` on its own, outside a text field, opens the tray. */
export function isTrayShortcut(event: KeyboardEvent): boolean {
  if (event.key !== "k" && event.key !== "K") return false;
  if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return false;
  const target = event.target as HTMLElement | null;
  return !target?.closest?.("input, textarea, select, [contenteditable=''], [contenteditable='true'], [role='textbox']");
}
